import type { DocumentNode } from '@apollo/client'

import {
  BulkCreatePlanningParticipantsDocument,
  BulkDeletePlanningParticipantsDocument,
  BulkUpdatePlanningParticipantsDocument,
  type PlanningValueObjectsParticipantRoleEnum,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Participant {
  id: string
  employee: { id: string }
  role: PlanningValueObjectsParticipantRoleEnum
}

interface SelectedParticipant {
  employeeId: string
  role: PlanningValueObjectsParticipantRoleEnum
}

interface UseParticipantsParams {
  periodId: string
  companyId: string
  planningId?: string
  refetchQueries?: DocumentNode[]
}

const useParticipants = ({
  periodId,
  companyId,
  planningId,
  refetchQueries,
}: UseParticipantsParams) => {
  const [bulkCreate, { loading: creating }] = useMutation(BulkCreatePlanningParticipantsDocument, {
    extractorFn: (result) => result.planningMutations.bulkCreatePlanningParticipant,
    refetchQueries,
  })

  const [bulkDelete, { loading: deleting }] = useMutation(BulkDeletePlanningParticipantsDocument, {
    extractorFn: (result) => result.planningMutations.bulkDeletePlanningParticipant,
    refetchQueries,
  })

  const [bulkUpdate, { loading: updating }] = useMutation(BulkUpdatePlanningParticipantsDocument, {
    extractorFn: (result) => result.planningMutations.bulkUpdatePlanningParticipant,
    refetchQueries,
  })

  const loading = creating || deleting || updating

  const showError = () => {
    app.setFlash({
      type: 'wrong',
      message: i18n.t('planning.manage_collaborators.error_message'),
    })
  }

  const save = async (
    selectedParticipants: SelectedParticipant[],
    currentParticipants: Participant[]
  ) => {
    const currentByEmployeeId = new Map(currentParticipants.map((p) => [p.employee.id, p]))
    const selectedEmployeeIds = new Set(selectedParticipants.map((c) => c.employeeId))

    const toAdd = selectedParticipants.filter((c) => !currentByEmployeeId.has(c.employeeId))
    const toRemove = currentParticipants.filter((p) => !selectedEmployeeIds.has(p.employee.id))
    const updates = selectedParticipants.flatMap((c) => {
      const existing = currentByEmployeeId.get(c.employeeId)
      return existing && existing.role !== c.role ? [{ id: existing.id, role: c.role }] : []
    })

    if (toAdd.length > 0) {
      const createResult = await bulkCreate({
        variables: {
          items: toAdd.map((c) => ({
            companyId,
            employeeId: c.employeeId,
            planningPeriodId: periodId,
            planningPlanningId: planningId ?? null,
            role: c.role,
          })),
        },
      })
      const handled = handleResult(createResult, showError)
      if (!handled.success) return { success: false }
    }

    if (toRemove.length > 0) {
      const deleteResult = await bulkDelete({
        variables: { ids: toRemove.map((p) => p.id) },
      })
      const handled = handleResult(deleteResult, showError)
      if (!handled.success) return { success: false }
    }

    if (updates.length > 0) {
      const updateResult = await bulkUpdate({
        variables: { updates },
      })
      const handled = handleResult(updateResult, showError)
      if (!handled.success) return { success: false }
    }

    app.setFlash({
      type: 'correct',
      message: i18n.t('planning.manage_collaborators.success_message'),
    })

    return { success: true }
  }

  return { save, loading }
}

export default useParticipants
